import Image from "next/image";
import { NextSeo } from "next-seo";

import { CoFounder } from "@/components/home/CoFounder";
import EditorDemoSection from "@/components/home/EditorDemoSection";
import FeaturesBento from "@/components/home/FeaturesSection";
import OfficialAppSection from "@/components/home/OfficialAppSection";
import { HeroButton } from "@/components/home/HeroButton";
import { StarRatings } from "@/components/home/StarRatings";
import StepsSection from "@/components/home/StepsSection";
import Testimonials from "@/components/home/Testimonials";
import FAQ from "@/components/shared/FAQ";
import Footer from "@/components/shared/Footer";
import HeroSection from "@/components/shared/HeroSection";
import JsonLd from "@/components/seo/JsonLd";
import { pageGraph } from "@/lib/seo/graph";
import { CDN_URL } from "@/consts";
import type { HomeContent } from "@/lib/i18n/content";
import { LOCALE_META, localizedPath, type Locale } from "@/lib/i18n/locales";
import { localeSeoProps } from "@/lib/i18n/seo";

type HomePageProps = {
  content: HomeContent;
  locale: Locale;
  path: string;
};

const HomePage = ({ content, locale, path }: HomePageProps) => {
  const heading = (
    <h1 className="text-center text-[31px] font-semibold text-gray-900 md:text-balance md:text-7xl md:tracking-[0.015em]">
      {content.heading.prefix}{" "}
      <span className="relative inline-block max-w-fit rounded-lg bg-[#335CFF] p-1 text-white sm:p-2">
        {content.heading.highlight}
        <Image
          src={`${CDN_URL}/landing/trusted-by-desktop.png`}
          alt="Crayo Logo"
          width={321}
          height={237}
          draggable={false}
          className="absolute -right-3 top-2 max-h-[140px] w-auto -translate-y-1/2 translate-x-full select-none max-xl:hidden"
        />
      </span>
      <br />
      {content.heading.suffix}
    </h1>
  );

  return (
    <div className="flex size-full flex-col overflow-hidden bg-white transition-all duration-200 ease-in-out">
      <JsonLd
        nodes={pageGraph({
          path: localizedPath(locale, path),
          name: "Crayo",
          description: content.seoDescription,
          faqs: content.faqs,
          ...(locale === "en" ? {} : { inLanguage: LOCALE_META[locale].htmlLang }),
        })}
      />
      <HeroSection
        topComponent={<StarRatings className="bg-transparent" textColor="text-[#1355FF]" />}
        heading={heading}
        body={content.body}
        mobileBody={content.mobileBody}
        button={<HeroButton />}
        wrapperStyle="gap-6 lg:gap-8"
      />
      <StepsSection />
      <EditorDemoSection />
      <FeaturesBento />
      <OfficialAppSection />
      <Testimonials />
      <CoFounder />
      <FAQ items={content.faqs} />
      <Footer />
      <NextSeo
        {...(locale === "en"
          ? {}
          : { title: content.seoTitle, description: content.seoDescription })}
        {...localeSeoProps(locale, path)}
      />
    </div>
  );
};

export default HomePage;
